import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading } from '@/components/ui/daisy';
import useFormList from '@/data/hook/shared/useFormList';
import { PickUnitInfo } from '@/models/Unit';
import UnitInlineForm from './UnitInlineForm';
import LimitedEditionWrapper from '../shared/LimitedEditionWrapper';

type UnitFormListProps = {
  unitsCount: number;
  onSubmit: (data: PickUnitInfo) => void;
  isLoading: boolean;
  isLimitedEdition?: boolean;
};

export default function UnitFormList({
  unitsCount,
  onSubmit,
  isLoading,
  isLimitedEdition = false,
}: UnitFormListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace.unitsTab',
  });
  const { openForms, addForm, removeForm, handleFormSubmit } =
    useFormList<PickUnitInfo>({ onSubmit });

  return (
    <div className="flex flex-col gap-6">
      <ConditionalRenderer condition={isLoading}>
        <FieldsetContainer className="flex justify-center items-center py-5">
          <Loading variant="spinner" size="md" color="primary" />
        </FieldsetContainer>
      </ConditionalRenderer>
      {openForms.map(formId => (
        <UnitInlineForm
          key={formId}
          onSubmit={handleFormSubmit(formId)}
          onCancel={() => removeForm(formId)}
          isLoading={isLoading}
          order={unitsCount + 1}
        />
      ))}
      <FieldsetContainer>
        <LimitedEditionWrapper
          isLimitedEdition={isLimitedEdition}
          className="w-full"
          placement="top"
        >
          <Button
            size="md"
            onClick={addForm}
            className="bg-base-300 w-full"
            disabled={isLimitedEdition}
          >
            <PlusIcon className="w-4 h-4" />
            {t('addUnit')}
          </Button>
        </LimitedEditionWrapper>
      </FieldsetContainer>
    </div>
  );
}
